export const getOptimizedBlob = async (croppieInstance, maxKb = 20) => {
  let currentQuality = 0.9; // Start with a high quality
  
  // 1. Get the initial crop
  let blob = await croppieInstance.result({
    type: 'blob',
    size: { width: 450, height: 450 },
    format: 'webp',
    quality: currentQuality
  });

  // 2. Loop and degrade quality until it fits the size constraint
  // (We stop at 0.1 quality to prevent an infinite loop on impossible images)
  while (blob.size > maxKb * 1024 && currentQuality > 0.1) {
    currentQuality -= 0.1; // Drop quality by 10% each attempt
    
    blob = await croppieInstance.result({
      type: 'blob',
      size: { width: 450, height: 450 },
      format: 'webp',
      quality: Number(currentQuality.toFixed(1)) // keep math clean
    });
  }

  console.log(`Final size: ${(blob.size / 1024).toFixed(2)} KB at ${currentQuality} quality`);
  return blob; // Ready for Firebase!
}

/**
 * Compresses an image file to WebP format, scaling it down to a max width/height
 * while maintaining aspect ratio, and looping to hit a target file size.
 * * @param {File} file - The original uploaded image file
 * @param {number} maxKb - The target maximum file size in Kilobytes (e.g., 20)
 * @returns {Promise<Blob>} - The optimized WebP blob
 */
export const getOptimizedImageNoCrop = async (file, maxKb = 20, MAX_DIMENSION = 450, forceToMax = false) => {
  // If it's a GIF, skip compression or handle differently
  if (file.type === 'image/gif') {
    return await compressGif(file, maxKb);
  }
  console.log(maxKb, MAX_DIMENSION, forceToMax);
  console.log('Original file size in KB:', file.size / 1024);
  if (!forceToMax && file.size <= maxKb * 1024) {
    console.log('File is already under the size limit, no optimization needed.');
    return file; // No need to optimize
  }
  return new Promise((resolve, reject) => {
    // 1. Create an image element to read the file dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      // Clean up memory
      URL.revokeObjectURL(objectUrl);

      // 2. Calculate the new dimensions while maintaining aspect ratio
      let { width, height } = img;
      // const MAX_DIMENSION = 450;

      if (forceToMax) {
        if (width > height) {
          height = Math.round((height * MAX_DIMENSION) / width);
          width = MAX_DIMENSION;
        } else {
          width = Math.round((width * MAX_DIMENSION) / height);
          height = MAX_DIMENSION;
        }
      } else {
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }
      }

      // 3. Draw the resized image onto a native HTML canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Helper function to turn the canvas into a Blob
      const getCanvasBlob = (quality) => {
        return new Promise((res) => {
          canvas.toBlob((blob) => res(blob), 'image/webp', quality);
        });
      };

      // 4. The Quality Loop (Same logic as before!)
      let currentQuality = 0.9;
      let blob = await getCanvasBlob(currentQuality);

      // Loop until it's under the maxKb limit, stopping at a floor of 0.5 to prevent it from looking awful
      while (blob.size > maxKb * 1024 && currentQuality > 0.5) {
        currentQuality -= 0.1;
        currentQuality = Math.round(currentQuality * 10) / 10; // Fix JS floating point math
        
        blob = await getCanvasBlob(currentQuality);
      }

      console.log(`Resized to ${width}x${height}. Final size: ${(blob.size / 1024).toFixed(2)} KB at ${currentQuality} quality`);
      resolve(blob);
    };

    img.onerror = (error) => {
      reject(new Error("Failed to load image for compression."));
    };

    // Trigger the load event
    img.src = objectUrl;
  });
};

import GIF from 'gif.js';

const compressGif = async (file, maxKb) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      const MAX_DIMENSION = 450;
      let { width, height } = img;

      // Calculate aspect ratio
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Initialize gif.js
      const gif = new GIF({
        workers: 2,
        quality: 10, // 1 is best, 20 is fastest/lowest quality
        width: width,
        height: height,
        workerScript: '/gif.worker.js' // Ensure this is in your /public folder
      });

      // Note: Drawing a GIF to canvas only draws the first frame.
      // To get ALL frames, we'd typically need a GIF decoder like 'libgif'.
      // For a simpler "Quick Fix", we draw the resized frame:
      ctx.drawImage(img, 0, 0, width, height);
      gif.addFrame(ctx, { copy: true, delay: 100 }); 

      gif.on('finished', (blob) => {
        URL.revokeObjectURL(objectUrl);
        resolve(blob);
      });

      gif.render();
    };

    img.onerror = () => reject(new Error("Failed to process GIF"));
    img.src = objectUrl;
  });
};